/** Modal confirmation for consequential admin actions (RTL, Esc/backdrop to cancel). */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Modal } from './ui/modal.js';
import { cn } from '../utils.js';

export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  cancelLabel = 'ביטול',
  tone = 'default',
  confirmPhrase,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** 'danger' paints the confirm button red — use for privileged/irreversible actions. */
  tone?: 'default' | 'danger';
  /**
   * When set, the word the admin must type before the confirm button unlocks.
   * For actions that destroy data, a single click is too easy to make by
   * accident — typing forces a deliberate second decision.
   */
  confirmPhrase?: string;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const phraseRef = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState('');
  const locked = confirmPhrase !== undefined && typed.trim() !== confirmPhrase;

  useEffect(() => {
    if (!open) return;
    // Reopening must never inherit the previous answer — otherwise the second
    // delete would be one click away, which is exactly what the phrase prevents.
    setTyped('');
    if (confirmPhrase !== undefined) phraseRef.current?.focus();
    else confirmRef.current?.focus();
  }, [open, confirmPhrase]);

  return (
    <Modal
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={locked}
            className={cn(
              'rounded-md px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40',
              tone === 'danger'
                ? 'bg-red-600 text-white hover:bg-red-700 disabled:hover:bg-red-600'
                : 'bg-primary text-primary-foreground hover:opacity-90',
            )}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="text-sm text-muted-foreground">{children}</div>
      {confirmPhrase !== undefined && (
        <label className="mt-4 block text-sm">
          <span className="mb-1 block text-muted-foreground">
            להמשך, הקלידו <span className="font-semibold text-foreground">{confirmPhrase}</span>
          </span>
          <input
            ref={phraseRef}
            type="text"
            autoComplete="off"
            aria-label={`הקלידו ${confirmPhrase} לאישור`}
            className="w-full rounded-md border border-input bg-popover px-3 py-2 text-base"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              // Enter is the natural reflex once the word is typed.
              if (e.key === 'Enter' && !locked) {
                e.preventDefault();
                onConfirm();
              }
            }}
          />
        </label>
      )}
    </Modal>
  );
}
